import { RecursiveStatic, TypeCustom } from '@diamond/shared/schema';
import { MultipartFile } from '@fastify/multipart';
import { Static, Type } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';

export const FEATURE_ACCESS_PARAMETER_KEY = 'feature_access_segments';

export const FEATURE_ACCESS_KEYS = [
  'cart_recommendation',
  'payment_proof_ocr',
  'homepage_recipe',
  'po_details_data',
] as const;

const FEATURE_ACCESS_KEY_PATTERN = '^[a-z0-9]+(?:_[a-z0-9]+)*$';

export const FeatureAccessKeySchema = Type.Union(
  FEATURE_ACCESS_KEYS.map((featureKey) => Type.Literal(featureKey))
);

const FeatureSegmentSchema = Type.Object(
  {
    features: Type.Array(FeatureAccessKeySchema, { uniqueItems: true }),
  },
  { additionalProperties: false }
);

const FeatureAccessReadSegmentSchema = Type.Object(
  {
    features: Type.Array(
      Type.String({
        minLength: 1,
        maxLength: 100,
        pattern: FEATURE_ACCESS_KEY_PATTERN,
      }),
      { uniqueItems: true }
    ),
  },
  { additionalProperties: false }
);

export const FeatureAccessConfigSchema = Type.Object(
  {
    version: Type.String({ minLength: 1 }),
    updated_at: Type.Integer({ minimum: 0 }),
    feature_segments: Type.Record(
      Type.RegEx(/^[a-z0-9]+(?:_[a-z0-9]+)*$/),
      FeatureSegmentSchema,
      { additionalProperties: false }
    ),
  },
  { additionalProperties: false }
);

export const FeatureAccessReadConfigSchema = Type.Object(
  {
    version: Type.String({ minLength: 1 }),
    updated_at: Type.Integer({ minimum: 0 }),
    feature_segments: Type.Record(
      Type.RegEx(/^[a-z0-9]+(?:_[a-z0-9]+)*$/),
      FeatureAccessReadSegmentSchema,
      { additionalProperties: false }
    ),
  },
  { additionalProperties: false }
);

export type FeatureAccessKey = Static<typeof FeatureAccessKeySchema>;
export type FeatureAccessConfig = Static<typeof FeatureAccessConfigSchema>;
export type FeatureAccessReadConfig = Static<
  typeof FeatureAccessReadConfigSchema
>;

export function isFeatureAccessConfig(
  value: unknown
): value is FeatureAccessConfig {
  return Value.Check(FeatureAccessConfigSchema, value);
}

export function isFeatureAccessReadConfig(
  value: unknown
): value is FeatureAccessReadConfig {
  return Value.Check(FeatureAccessReadConfigSchema, value);
}

export const FEATURE_SEGMENT_KEY_PATTERN = '^[a-z0-9]+(?:_[a-z0-9]+)*$';
export const MAX_BULK_SHIPTO_SEGMENTS = 500;

const UpsertRemoteConfigFeatureSegmentSchema = Type.Object(
  {
    features: Type.Array(Type.String({ minLength: 1 })),
  },
  { additionalProperties: false }
);

export const FeatureAccessCrudSchema = {
  path: '/feature-access',
  getRemoteConfig: {
    path: '/remote-config',
    response: Type.Object(
      {
        data: Type.Object(
          {
            parameter_key: Type.Literal(FEATURE_ACCESS_PARAMETER_KEY),
            ...FeatureAccessReadConfigSchema.properties,
          },
          { additionalProperties: false }
        ),
      },
      { additionalProperties: false }
    ),
  },
  upsertRemoteConfig: {
    path: '/remote-config/upsert',
    body: Type.Object(
      {
        version: Type.String({ minLength: 1 }),
        feature_segments: Type.Record(
          Type.String(),
          UpsertRemoteConfigFeatureSegmentSchema
        ),
      },
      { additionalProperties: false }
    ),
    response: Type.Object(
      {
        data: Type.Object(
          {
            parameter_key: Type.Literal(FEATURE_ACCESS_PARAMETER_KEY),
            version: Type.String({ minLength: 1 }),
            updated_at: Type.Integer({ minimum: 0 }),
            published: Type.Literal(true),
          },
          { additionalProperties: false }
        ),
      },
      { additionalProperties: false }
    ),
  },
  exportShiptoSegments: {
    path: '/shipto-segments/export',
    query: Type.Object(
      {
        sap_no_shipto: Type.Optional(
          Type.Integer({ minimum: 1, maximum: 2_147_483_647 })
        ),
        feature_segment: Type.Optional(
          Type.String({
            minLength: 1,
            maxLength: 100,
            pattern: '^[a-z0-9]+(?:_[a-z0-9]+)*$',
          })
        ),
      },
      { additionalProperties: false }
    ),
    response: Type.String({ format: 'binary' }),
  },
  assignShiptoSegment: {
    path: '/shipto-segments/assign',
    body: Type.Object({
      sap_no_shipto: Type.Integer({ minimum: 1 }),
      feature_segment: Type.String({ minLength: 1, maxLength: 100 }),
    }),
    response: Type.Object({
      data: Type.Object({
        id: Type.String({ format: 'uuid' }),
        sap_no_shipto: Type.Integer(),
        feature_segment: Type.String(),
        status: Type.Union([
          Type.Literal('created'),
          Type.Literal('restored'),
          Type.Literal('unchanged'),
        ]),
      }),
    }),
  },
  bulkAssignShiptoSegments: {
    path: '/shipto-segments/bulk-assign',
    body: Type.Object({
      feature_segment: Type.String({
        minLength: 1,
        maxLength: 100,
        pattern: FEATURE_SEGMENT_KEY_PATTERN,
      }),
      sap_no_shiptos: Type.Array(Type.Integer({ minimum: 1 }), {
        minItems: 1,
        maxItems: MAX_BULK_SHIPTO_SEGMENTS,
      }),
    }),
    response: Type.Object({
      data: Type.Object({
        feature_segment: Type.String(),
        created: Type.Integer({ minimum: 0 }),
        restored: Type.Integer({ minimum: 0 }),
        unchanged: Type.Integer({ minimum: 0 }),
        failed: Type.Integer({ minimum: 0 }),
        errors: Type.Array(
          Type.Object({
            sap_no_shipto: Type.Integer(),
            reason: Type.String(),
          })
        ),
      }),
    }),
  },
  deleteShiptoSegment: {
    path: '/shipto-segments/:sapNoShipto/:segment',
    params: Type.Object({
      sapNoShipto: Type.Integer({ minimum: 1 }),
      segment: Type.String({
        minLength: 1,
        maxLength: 100,
        pattern: FEATURE_SEGMENT_KEY_PATTERN,
      }),
    }),
    response: Type.Object({
      data: Type.Object({
        sap_no_shipto: Type.Integer(),
        feature_segment: Type.String(),
        status: Type.Literal('deleted'),
      }),
    }),
  },
  bulkDeleteShiptoSegments: {
    path: '/shipto-segments/bulk-delete',
    body: Type.Object({
      feature_segment: Type.String({
        minLength: 1,
        maxLength: 100,
        pattern: FEATURE_SEGMENT_KEY_PATTERN,
      }),
      sap_no_shiptos: Type.Array(Type.Integer({ minimum: 1 }), {
        minItems: 1,
        maxItems: 1000,
      }),
    }),
    response: Type.Object({
      data: Type.Object({
        feature_segment: Type.String(),
        deleted: Type.Integer({ minimum: 0 }),
        unchanged: Type.Integer({ minimum: 0 }),
        failed: Type.Integer({ minimum: 0 }),
        errors: Type.Array(
          Type.Object({
            sap_no_shipto: Type.Integer(),
            reason: Type.String(),
          })
        ),
      }),
    }),
  },
  importShiptoSegments: {
    path: '/shipto-segments/import',
    body: Type.Object({
      file: TypeCustom.Custom<MultipartFile>(),
      dry_run: Type.Optional(Type.Boolean()),
    }),
    response: Type.Object({
      data: Type.Object({
        dry_run: Type.Boolean(),
        total_rows: Type.Integer({ minimum: 0 }),
        created: Type.Integer({ minimum: 0 }),
        restored: Type.Integer({ minimum: 0 }),
        deleted: Type.Integer({ minimum: 0 }),
        unchanged: Type.Integer({ minimum: 0 }),
      }),
    }),
  },
};

export type FeatureAccessCrud = RecursiveStatic<typeof FeatureAccessCrudSchema>;
